// config.js - runtime config. Only the keys below are read at runtime; brand
// strings (page title, og tags, footer text) live where they're used.

export const CONFIG = {

  supabase: {
    url:    'https://cejfvzczsyrhrtngdyzx.supabase.co',
    anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNlamZ2emN6c3lyaHJ0bmdkeXp4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc0MTE2OTEsImV4cCI6MjA5Mjk4NzY5MX0.MXU52qWfXQTaSnECaOqTiKOiQuEDkGeAvFsps1xkI14',
  },

  // Rolled over 2026-07-24. The `seasons` table already had 2026-2027 flagged
  // is_current with 618 memberships and 37 team anchors imported, while this
  // constant still pointed at 2025-2026 - so the app was reading last season
  // while the database considered the new one live. They now agree.
  // Last season's data is untouched and still reachable by season pickers.
  //
  // Flipped BACK to 2025-2026 at some point after that, and restored 2026-08-10.
  // The cause is worth knowing, because it will happen again: there are two
  // season switchers and only one of them is local.
  //   - home badge "Change"  -> writes localStorage only, your view alone
  //   - Dashboard season pill -> DB.seasons.setActive(), changes the live
  //                              season for THIS CLUB (and only this club)
  //
  // That second line used to read "for every user of the club" and was taken,
  // on 4 Nov 2026, to mean every CLUB: it does not. setActive writes
  // clubs.current_season_id and getCurrent reads the club's own choice, which
  // is the fix for the one-club-rolls-over-everybody bug. The shared
  // seasons.is_current flag survives only as a default for a club that has
  // never chosen, and since 4 Nov nobody can edit it: seasons is insert-and-
  // read only (migration 20261104150000).
  // Opening the second one to glance at last season silently rolls the whole
  // club back, and because the stored override survives the reload the director
  // still sees the new season and never notices. The only tell is the home
  // badge reading "Past season" instead of "Change".
  season: {
    // `current` is what every season-scoped screen reads, and the director can
    // point it at a PAST season from the home badge. `liveId` stays on the real
    // season so the app can say "you are looking at history" rather than
    // silently showing last year as if it were now.
    current:    '2026-2027',
    liveId:     '2026-2027',
    label:      '2026-2027',
    startDate:  '2026-09-01',
    endDate:    '2027-06-30',
  },

  access: {
    // Sentinel value compared in app.js / pdf-shared.js to identify ratings
    // entered by the director. Not the real director login code - that lives
    // server-side as the DIRECTOR_CODE Edge Function secret.
    directorCode: 'DIRECTOR',
    // Sentinel team_code for the limited co-director's own evaluations. Distinct
    // from 'DIRECTOR' on purpose: it makes her evals count at normal 1x coach
    // weight (only 'DIRECTOR' triggers the 1.5x), as their own rater.
    coDirectorCode: 'CODIRECTOR',
    // Sentinel team_code for a line coordinator's fast banded pass. Its own
    // sentinel for two reasons: it counts at normal 1x coach weight (only
    // 'DIRECTOR' triggers the 1.5x), and band drafting has to be able to leave
    // it out. A coordinator's marks come OUT of a band, so feeding them back
    // in would make the band self-referential: simulated over five seasons it
    // freezes solid, and a squad that improved by three quarters of a level
    // kept the band it started with.
    coordinatorCode: 'COORDINATOR',
  },

  scoring: {
    // How many times a director rating counts vs one coach rating in weighted
    // averages. The PDF report's options modal can override this per export.
    // 1.5 (director decision 2026-06-16): director leads but does not fully
    // double, so a bulk director-eval pass can't over-dominate lower teams.
    // Kept in sync with the player_season_summary view (migration ...0003).
    directorWeight:     1.5,
    levelNames:         ['Developing', 'Emerging', 'Secure', 'Advanced', 'Impactful'],
    // Distance from the cohort average within which "-" replaces an
    // above/below indicator on the comparison dots.
    atAverageThreshold: 0.4,
  },

  // Active UI language for V6 strings (src/strings.js). V5 strings stay
  // hardcoded until retrofitted lazily - see V6_DESIGN §5.8.
  i18n: {
    lang: 'en',
  },

  // Player Page (Module 1, V6_DESIGN §6).
  player: {
    // Director decision 2026-06-11: players see an AGGREGATED view (average of
    // all their published scores) and never individual evaluators, so coaches
    // who are also club parents stay unidentifiable. Flip to true only if the
    // director explicitly wants contributor names listed on the player view.
    showCoachNames: false,
    codePrefix: 'PA-',     // access codes minted by player-auth; the gate routes on it
    // The season-visibility switch (director model, V6_DESIGN 6.4): players
    // see ONLY the current season's published evaluations - last season and
    // any historical imports go dark automatically at the season rollover.
    currentSeasonOnly: true,
  },

  // Coach Hub (Module 6, V6_DESIGN §11).
  staffHub: {
    codePrefix: 'ST-',     // personal staff codes; the gate routes them to staff-auth
  },

  // Squad codes: ONE code shared by a whole team, who then pick their own name
  // on the page. Prefixes are listed here rather than read from the club
  // registry in clubs.js, because that registry is cosmetic and must never
  // decide what a code can do.
  squad: {
    codePrefixes: ['VIC'],   // Royal Victory H.C. (VicD1 = Dames 1)
  },

  // FitnessTimer protocols (V6_DESIGN §5.6) - the generalised Bronco engine
  // in src/fitness-timer.js. Each protocol is pure config; new fitness tests
  // (40-Second Dribble, Yo-Yo, Cooper) are added here, not as new timer code.
  fitness: {
    tickMs: 100, // UI refresh interval while running; timing itself is timestamp-based
    protocols: {
      bronco: {
        id:    'bronco',
        label: 'Bronco test',
        mode:  'stopwatch', // counts up; players are tapped in as they finish
        waves: true,        // consumer may reset the clock while keeping recorded times
        beepAtSecondsLeft: [], // countdown-only feature; unused for stopwatch
        // Plausibility (Phase 0c Fix 2). A finish tapped before
        // plausibleFinishMs makes the tester confirm (mistap guard) and is
        // flagged in the director's results; analysis (player_season_summary)
        // hard-drops anything under 30 s at the SQL level.
        plausibleFinishMs: 310000, // ~5:10 - fastest realistic girls time this cycle
      },
      // Baseline Template B - 40-Second Dribble (Module 2). Counts DOWN from
      // 40 s with warning beeps; auto-finishes at zero.
      dribble40: {
        id:    'dribble40',
        label: '40-Second Dribble',
        mode:  'countdown',
        durationMs: 40000,
        waves: false,
        beepAtSecondsLeft: [3, 2, 1, 0],
      },
    },
  },

  // Baseline Testing (Module 2, V6_DESIGN §7). The exercise catalogue is pure
  // config - a new exercise is a row here + a setup illustration, no new UI.
  // template: 'stopwatch' (Template A) | 'reps' (B) | 'gates' (C) |
  //           'gates_dynamic' (C, push->shot split). better: lower or higher wins.
  baseline: {
    codePrefix: 'BT-',   // tester codes; the gate routes them to baseline-tester-auth
    exercises: [
      { key: 'illinois_no_ball',   name: 'Illinois Agility (no ball)',   domain: 'Agility & Speed', template: 'stopwatch',     better: 'min' },
      { key: 'illinois_with_ball', name: 'Illinois Agility (with ball)', domain: 'Agility & Speed', template: 'stopwatch',     better: 'min' },
      { key: 't_test_no_ball',     name: 'T-Test (no ball)',             domain: 'Agility & Speed', template: 'stopwatch',     better: 'min' },
      { key: 't_test_with_ball',   name: 'T-Test (with ball)',           domain: 'Agility & Speed', template: 'stopwatch',     better: 'min' },
      { key: 'dribble_40s',        name: '40-Second Dribble',            domain: 'Distribution',    template: 'reps',          better: 'max', protocol: 'dribble40' },
      { key: 'static_push',        name: 'Static Push Test',             domain: 'Distribution',    template: 'gates',         better: 'max', durationMs: 10000, ballCap: 8 },
      { key: 'static_shot',        name: 'Static Shot Test',             domain: 'Distribution',    template: 'gates',         better: 'max', durationMs: 15000, ballCap: 8 },
      { key: 'dynamic_dist',       name: 'Dynamic Distribution Test',    domain: 'Distribution',    template: 'gates_dynamic', better: 'max', durationMs: 50000, pushCount: 4, shotCount: 4 },
      // Recorded in the field before it existed here, so 55 results were being
      // scored against a silent 'higher is better' default and shown by raw key.
      // It IS a rep count (observed range 21-70), so 'max' was right by luck.
      { key: 'yard_stick_dribble', name: 'Yard Stick Dribble',           domain: 'Distribution',    template: 'reps',          better: 'max' },
    ],
  },

  ageGroups: ['U11–U12', 'U14', 'U16–U19'],

  // Twizzit import - the per-line team pattern turns "Girls U14-3" into the
  // team id "U14G3" (and the same for boys). Everything else about the import
  // (column positions, valid functions, accent normalisation) is in import.js.
  twizzit: {
    teamPatterns: {
      girls: { regex: /^Girls U(\d+)-(\d+)$/i, idLetter: 'G' },
      boys:  { regex: /^Boys U(\d+)-(\d+)$/i,  idLetter: 'B' },
    },
  },

};

// Active language: a saved choice wins; otherwise default a Dutch-locale
// browser to Dutch (the club is Flemish), else English. Object.freeze below is
// shallow, so CONFIG.i18n.lang stays writable at runtime for the EN/NL toggle.
try {
  const saved = localStorage.getItem('khc_lang');
  if (saved === 'en' || saved === 'nl') CONFIG.i18n.lang = saved;
  else if ((navigator.language || '').toLowerCase().startsWith('nl')) CONFIG.i18n.lang = 'nl';
} catch { /* localStorage/navigator unavailable - keep the default 'en' */ }

Object.freeze(CONFIG);
